import { useState } from "react";
import CodeReviewPanel from "./CodeReviewPanel";
import LedgerPanel, { type HeaderSlots } from "./LedgerPanel";
import BobOverview from "./bob/BobOverview";
import BobReports from "./bob/BobReports";
import ReviewTally from "./bob/ReviewTally";
import { locationReport, type ReportTab, useReportNav } from "./bob/reportNav";
import "./bob/bobTheme.css";
import "./bob/bobConsole.css";

type Props = {
  project: string;
  /** Display name for the project; the header falls back to the id when unknown. */
  projectName?: string;
  onClose: () => void;
  /** Switch the page to another project, from the ledger's pack rail. */
  onSelectProject?: (id: string) => void;
};

// The tab strip is data: a third view is an entry here, not another button.
// Which panel an id renders stays in the body, because the panels take different props.
const TABS = [
  { id: "ledger", label: "Console", title: "What Bob has found on the tracked branch" },
  { id: "overview", label: "Overview", title: "Bob's posture across packs" },
  { id: "reports", label: "Reports", title: "Every review Bob ran, with its report" },
  { id: "queue", label: "Queue", title: "Automatic PR code review queue" },
] as const satisfies ReadonlyArray<{ id: ReportTab; label: string; title: string }>;

// The app has one theme, a dark one, so the console uses its dark tokens.
const THEME = "dark";

// Full-page Code Review view. The PR review queue is project-level (not tied to
// a single conversation), so it lives on its own route (/review/<project>) — a
// hard reload restores it instead of bouncing back to the chat dashboard.
//
// Ledger first: the queue is what Bob is doing right now, the ledger is what he
// has found, and the second is what someone opening this page came to read.
// One tab is mounted at a time, so the queue's poll only runs while it is shown.
export default function CodeReviewPage({ project, projectName, onClose, onSelectProject }: Props) {
  const [tab, setTab] = useState<ReportTab>(() => locationReport().tab);
  const nav = useReportNav(project, projectName, setTab);
  // The ledger renders its repo, branch, last review and actions into these.
  const [repoSlot, setRepoSlot] = useState<HTMLElement | null>(null);
  const [factsSlot, setFactsSlot] = useState<HTMLElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const header: HeaderSlots = { repo: repoSlot, facts: factsSlot, actions: actionsSlot };
  // The Overview spans every pack, so the header names no pack there.
  const packHeader = tab !== "overview";
  return (
    <div className="review-page bob" data-theme={THEME} data-tab={tab}>
      <header className="review-page-header bob-head">
        <div className="bob-head-row">
          <div className="bob-brand">
            <button type="button" className="review-page-back" onClick={onClose} aria-label="Back to chat">←</button>
            <div className="bob-logo" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
              </svg>
            </div>
            {packHeader ? (
              <div className="bob-titles">
                <span className="bob-titles-kicker">Bob code review</span>
                <h1>{projectName ?? project} <span ref={setRepoSlot} /></h1>
              </div>
            ) : (
              <div className="bob-titles"><h1>Bob code review</h1></div>
            )}
          </div>
          {packHeader && (
            <>
              <div className="bob-head-facts">
                <span className="bob-head-slot" ref={setFactsSlot} />
                <ReviewTally project={project} isAdmin={nav.isAdmin} />
              </div>
              <div className="bob-head-actions" ref={setActionsSlot} />
            </>
          )}
        </div>
        <div className="bob-tabs" role="tablist" aria-label="Code review views">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`bob-tab${tab === t.id ? " active" : ""}`}
              title={t.title}
              onClick={() => nav.openTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <div className="bob-page-body">
        {tab === "ledger" && <LedgerPanel project={project} projectName={projectName} header={header} onSelectProject={onSelectProject} runNav={nav} />}
        {tab === "overview" && <BobOverview nav={nav} />}
        {tab === "reports" && <BobReports nav={nav} />}
        {tab === "queue" && <CodeReviewPanel project={project} active={true} />}
      </div>
    </div>
  );
}
